/**
 * The Pages tree, in either shape the shell shows it (see `docsTree.tsx`),
 * with the desktop's tree behaviour: placed types and typed pages (the type
 * resolver over the project's tracker room), Place type, Set type, and the
 * create menu the header's New control shows.
 *
 * In the fly-over, picking a page closes it -- the point of the fly-over is to
 * jump. Docked, the pane stays through every pick; on a phone the docked pane
 * is an overlay, so there it closes on a pick as the fly-over does. Expanding
 * a page never closes anything: that is browsing.
 *
 * The tree stays mounted while hidden, so the header's New control (which the
 * tree builds) works with the tree put away.
 */
import type { ReactNode } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import {
  CollabSidebar,
  HeaderIconButton,
  PagesSectionEntries,
  type CollabSidebarCreateMenu,
  type PagesSectionEntry,
  type SharedDocument,
} from '@nimbalyst/collab-bundle/docs-ui';
import { useDocsShell, type DocsTreeMode } from '../collab/CollabDocsShell';
import { DecorativeIcon } from '../organization/DecorativeIcon';
import { isPhoneViewport } from '../phoneViewport';
import { consoleRoutePatterns, orgWikiPath, orgWikiTypesPath } from '../routing';
import { homePageId } from './pagesHome';
import { usePagesTrackers } from './PagesTrackers';

/** Which fixed row the open route is: Search, Types, or the project's Home page. */
function activeEntry(pathname: string, activeDocumentId: string | null | undefined, projectId: string): PagesSectionEntry | null {
  if (matchPath(consoleRoutePatterns.wiki, pathname)) return 'search';
  if (matchPath(consoleRoutePatterns.wikiTypes, pathname)) return 'types';
  return activeDocumentId === homePageId(projectId) ? 'home' : null;
}

export function PagesTreePanel({
  mode,
  activeDocumentId,
  activeItemId,
  activeTypeId,
  scopeName,
  scopePath,
  onShowHome,
  onClose,
  onSetPageType,
  registerCreateMenu,
}: {
  /** Null while the tree is put away. */
  mode: DocsTreeMode | null;
  activeDocumentId?: string | null;
  activeItemId?: string | null;
  activeTypeId?: string | null;
  scopeName: (afterPick: () => void) => ReactNode;
  scopePath: ReactNode;
  onShowHome: () => void;
  onClose: () => void;
  onSetPageType?: (document: SharedDocument) => void;
  registerCreateMenu: (menu: CollabSidebarCreateMenu | null) => void;
}) {
  const { resolver, archiveItem } = usePagesTrackers();
  const { team, projectId, navigate } = useDocsShell();
  const { pathname } = useLocation();
  const closeOnPick = mode === 'flyover' || isPhoneViewport();
  const afterPick = () => {
    if (closeOnPick) onClose();
  };
  return (
    <div
      className="browser-collab-tree-panel h-full min-h-0"
      data-tree-mode={mode ?? 'hidden'}
      // Capture, not bubble: the page row stops propagation of its own click.
      // A modified click is a request for a new tab and leaves the tree as is.
      onClickCapture={(event) => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if ((event.target as Element).closest('.file-tree-file')) afterPick();
      }}
    >
      <CollabSidebar
        activeDocumentId={activeDocumentId}
        activeItemId={activeItemId}
        activeTypeId={activeTypeId}
        // The entries are `.file-tree-file` rows, so a pick closes the fly-over above.
        sectionEntries={(
          <PagesSectionEntries
            active={activeEntry(pathname, activeDocumentId, projectId)}
            onOpenHome={onShowHome}
            onOpenSearch={() => navigate(orgWikiPath(team, projectId))}
            onOpenTypes={() => navigate(orgWikiTypesPath(team, projectId))}
          />
        )}
        scopeName={scopeName(afterPick)}
        scopePath={scopePath}
        typeResolver={resolver}
        onArchiveItem={archiveItem}
        onSetPageType={onSetPageType}
        registerCreateMenu={registerCreateMenu}
        headerActions={(
          <HeaderIconButton
            className="project-pane-toggle size-[26px] text-nim-faint"
            label={mode === 'docked' ? 'Hide page tree' : 'Close page tree'}
            onClick={onClose}
          >
            <DecorativeIcon icon="left_panel_close" size={17} />
          </HeaderIconButton>
        )}
      />
    </div>
  );
}
